/**
 * NOS-118: Form for editing org AI profile (mission, vision, values, tone, etc.).
 */
import { useState, useEffect } from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import type { OrgAIProfile, TonePreference } from "@/types/orgProfile"

const TONE_OPTIONS: { value: TonePreference; label: string }[] = [
  { value: "conversational", label: "Conversational" },
  { value: "formal", label: "Formal" },
  { value: "inspirational", label: "Inspirational" },
  { value: "professional", label: "Professional" },
]

export interface OrgAIProfileFormProps {
  profile: OrgAIProfile | null
  isLoading: boolean
  onSave: (data: Partial<OrgAIProfile>) => Promise<void>
}

const defaultValues: Partial<OrgAIProfile> = {
  mission: "",
  vision: "",
  values: "",
  who_served: "",
  years_operating: "",
  impact_stats: "",
  tone: null,
}

export function OrgAIProfileForm({
  profile,
  isLoading,
  onSave,
}: OrgAIProfileFormProps) {
  const [form, setForm] = useState<Partial<OrgAIProfile>>(defaultValues)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (profile) {
      setForm({
        mission: profile.mission ?? "",
        vision: profile.vision ?? "",
        values: profile.values ?? "",
        who_served: profile.who_served ?? "",
        years_operating: profile.years_operating ?? "",
        impact_stats: profile.impact_stats ?? "",
        tone: profile.tone ?? null,
      })
    } else {
      setForm(defaultValues)
    }
  }, [profile])

  const handleChange = (key: keyof OrgAIProfile, value: string | number | null) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      const payload: Partial<OrgAIProfile> = {
        mission: form.mission?.trim() || null,
        vision: form.vision?.trim() || null,
        values: form.values?.trim() || null,
        who_served: form.who_served?.trim() || null,
        years_operating: form.years_operating?.trim() || null,
        impact_stats: form.impact_stats?.trim() || null,
        tone: (form.tone as TonePreference | null) || null,
      }
      await onSave(payload)
      toast.success("AI profile saved")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save")
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground py-4">Loading profile…</div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="mission">Mission</Label>
        <Textarea
          id="mission"
          value={form.mission ?? ""}
          onChange={(e) => handleChange("mission", e.target.value)}
          placeholder="Your organization's mission statement"
          rows={3}
          className="resize-none"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="vision">Vision</Label>
        <Textarea
          id="vision"
          value={form.vision ?? ""}
          onChange={(e) => handleChange("vision", e.target.value)}
          placeholder="Your long-term vision"
          rows={2}
          className="resize-none"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="values">Values</Label>
        <Textarea
          id="values"
          value={form.values ?? ""}
          onChange={(e) => handleChange("values", e.target.value)}
          placeholder="Core values (one per line or comma-separated)"
          rows={3}
          className="resize-none"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="who_served">Who you serve</Label>
        <Input
          id="who_served"
          value={form.who_served ?? ""}
          onChange={(e) => handleChange("who_served", e.target.value)}
          placeholder="e.g. Veterans, families, low-income communities"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="years_operating">Years operating</Label>
        <Input
          id="years_operating"
          type="text"
          value={form.years_operating ?? ""}
          onChange={(e) => handleChange("years_operating", e.target.value)}
          placeholder="e.g. 10"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="impact_stats">Impact stats</Label>
        <Textarea
          id="impact_stats"
          value={form.impact_stats ?? ""}
          onChange={(e) => handleChange("impact_stats", e.target.value)}
          placeholder="Key stats (e.g. People served: 500, Funds raised: $1M)"
          rows={2}
          className="resize-none"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="tone">Tone preference</Label>
        <Select
          value={(form.tone as string) ?? ""}
          onValueChange={(v) => handleChange("tone", v ? (v as TonePreference) : null)}
        >
          <SelectTrigger id="tone">
            <SelectValue placeholder="Select tone" />
          </SelectTrigger>
          <SelectContent>
            {TONE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" disabled={saving}>
        {saving ? "Saving…" : "Save changes"}
      </Button>
    </form>
  )
}
